import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  EnvironmentProviders,
  InjectionToken,
  LOCALE_ID,
  Provider,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import {
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { provideCoreGlobalSearchDataAccess } from '@maximizer/core/global-search/data-access';
import { provideCoreSettingsDataAccess } from '@maximizer/core/settings/data-access';
import { provideAiDataAccess } from '@maximizer/ai/data-access';
import {
  ContextService,
  FeatureService,
  GlobalStore,
  provideSharedDataAccess,
  TokenService,
} from '@maximizer/core/shared/data-access';
import { CDN_URL, OPEN_AI, Session } from '@maximizer/core/shared/domain';
import {
  InsightsService,
  provideInsights,
} from '@maximizer/core/shared/insights';
import { provideTranslateServiceForApp } from '@maximizer/core/shared/ui/config';
import { provideKendo } from '@maximizer/core/shared/ui/kendo';
import {
  OAuth2Service,
  provideOAuth2DataAccess,
} from '@maximizer/oauth2/data-access';
import {
  SyncConfigurationFacade,
  UserSyncConfiguration,
  provideOutlookSyncDataAccess,
} from '@maximizer/outlook-sync/data-access';
import { provideOutlookCaseDataAccess } from '@maximizer/outlook/case/data-access';
import { provideOutlookInteractionLogDataAccess } from '@maximizer/outlook/interaction-log/data-access';
import { provideOutlookMainDataAccess } from '@maximizer/outlook/main/data-access';
import { provideOutlookNoteDataAccess } from '@maximizer/outlook/note/data-access';
import { provideOutlookOpportunityDataAccess } from '@maximizer/outlook/opportunity/data-access';
import {
  AbEntryService,
  FrenchMessageService,
  MailboxType,
  OutlookService,
  OutlookStore,
  PreferencesService,
  VerticalService,
  provideOutlookSharedDataAccess,
  InitializationService,
} from '@maximizer/outlook/shared/data-access';
import { provideOutlookTaskDataAccess } from '@maximizer/outlook/task/data-access';
import { OutlookAiTokenService } from '@maximizer/outlook/ai/data-access';
import { provideOutlookTimelineDataAccess } from '@maximizer/outlook/timeline/data-access';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { provideEffects } from '@ngrx/effects';
import { getState } from '@ngrx/signals';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from '@progress/kendo-angular-l10n';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { ADDIN_VERSION } from './app.version';
import { OutlookAuthService } from './auth/auth.service';
import {
  getOfficeContextSafe,
  getOfficeLocale,
  isOfficeFrench,
} from './office-locale';
import { v4 as uuidv4 } from 'uuid';

export const INSTRUMENTATION_KEY = new InjectionToken<string>(
  'instrumentationKey',
);
export const APP_STORE_OAUTH_URL = new InjectionToken<string>(
  'appStoreOAthUrl',
);
export const APP_STORE_CONFIGURATION_URL = new InjectionToken<string>(
  'appStoreConfigurationUrl',
);
export const INTEGRATION_WEBHOOK_URL = new InjectionToken<string>(
  'integrationWebhook',
);
export const WEBHOOK_CREATE_EARLY_ACCESS = new InjectionToken<string>(
  'webhookEarlyAccess',
);
export const WEBHOOK_READ_EARLY_ACCESS = new InjectionToken<string>(
  'webhookEarlyAccess',
);
export const GRAPH_API_SCOPES = new InjectionToken<string>('graphApiScopes');
export const GRAPH_API_URI = new InjectionToken<string>('graphApiUri');
export const ON_PREM_CLIENT_ID = new InjectionToken<string[]>('onPremClientId');

function getOutlookClientType(): string {
  const officeContext = getOfficeContextSafe();
  const platform = officeContext?.platform;
  const hostName = officeContext?.mailbox?.diagnostics?.hostName;

  if (platform === Office.PlatformType.OfficeOnline) {
    return 'Browser';
  }

  if (platform === Office.PlatformType.PC) {
    return hostName === 'newOutlookWindows' ? 'Windows New' : 'Windows Classic';
  }

  if (platform === Office.PlatformType.Mac) {
    return 'Mac';
  }

  return 'Unknown';
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(`${logLevel}: ${message}`);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: true,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
}

async function initializeAbEntryPreferences(
  abEntryService: AbEntryService,
  outlookStore: OutlookStore,
  globalStore: GlobalStore,
): Promise<void> {
  const sessionInfo = globalStore.session();

  if (!sessionInfo) {
    return;
  }

  try {
    const preferences = await firstValueFrom(
      abEntryService.getLastContactedPreferences(),
    );
    outlookStore.setLastContactDatePreferences(preferences);
  } catch (error) {
    console.error('Error loading last contact date preferences:', error);
  }
}

async function initializeOutlookUserPreferences(
  preferencesService: PreferencesService,
  sessionInfo: Session,
  isCloud: boolean,
  syncConfig: UserSyncConfiguration | undefined,
): Promise<void> {
  try {
    const preferenceExists = await firstValueFrom(
      preferencesService.getPreferences(),
    );

    if (!sessionInfo) return;

    if (preferenceExists) {
      await firstValueFrom(
        preferencesService.applySaveOnSendDefault(syncConfig),
      );
      return;
    }

    const userHasInsertDocumentRights =
      sessionInfo.user.rights.documents.insert ?? false;
    preferencesService.defaultPreferences.saveNow = !isCloud
      ? true
      : !syncConfig?.enabled && userHasInsertDocumentRights;

    await firstValueFrom(
      preferencesService.updatePreferences(
        preferencesService.defaultPreferences,
      ),
    );
  } catch (error) {
    console.error('Error initializing outlook user preferences:', error);
  }
}

async function initializeSyncConfiguration(
  syncFacade: SyncConfigurationFacade,
  outlookStore: OutlookStore,
  sessionInfo: Session,
  isCloud: boolean,
): Promise<UserSyncConfiguration | undefined> {
  if (!isCloud) return undefined;

  const existing = outlookStore.syncConfiguration();
  if (existing) return existing;

  let syncConfig: UserSyncConfiguration | undefined;
  try {
    syncConfig = await firstValueFrom(
      syncFacade.getConfiguration(sessionInfo.user.id, sessionInfo.workspace),
    );
  } catch (error) {
    // Bootstrap is best-effort; downstream UI surfaces auth/config failure when sync is actually used.
    console.warn('Bootstrap: failed to initialize sync configuration', error);
    return undefined;
  }

  if (syncConfig) {
    outlookStore.updateSyncConfiguration(syncConfig);
  }

  return syncConfig;
}

async function initializeUserPreferences(
  preferencesService: PreferencesService,
  abEntryService: AbEntryService,
  outlookStore: OutlookStore,
  globalStore: GlobalStore,
  sessionInfo: Session,
  isCloud: boolean,
  syncConfig: UserSyncConfiguration | undefined,
): Promise<void> {
  await initializeOutlookUserPreferences(
    preferencesService,
    sessionInfo,
    isCloud,
    syncConfig,
  );

  await initializeAbEntryPreferences(abEntryService, outlookStore, globalStore);
}

function createDatabaseVerticalInitializer(
  verticalService: VerticalService,
): () => Promise<void> {
  return async function initializeDatabaseVertical(): Promise<void> {
    await firstValueFrom(verticalService.getDatabaseVertical());
  };
}

function initializeMinimumLogLevel(
  globalStore: GlobalStore,
  featureService: FeatureService,
  contextService: ContextService,
) {
  const session = globalStore.session();
  if (!session) {
    return;
  }

  const logLevel = featureService.getVariation(
    environment.minLogLevelFeatureFlag,
    'Information',
  );
  const eventLevel =
    SeverityLevel[logLevel as keyof typeof SeverityLevel] ??
    SeverityLevel.Information;

  const state = getState(globalStore);
  const currentInsights = state.insightsConfiguration['default'] ?? {
    applicationInsights: {
      instrumentationKey: environment.instrumentationKey,
      disableFlushOnBeforeUnload: true,
      disableFlushOnUnload: true,
    },
    eventLevel,
    trackPageView: true,
  };

  const baseCustomDimensions: Record<string, string> = {
    alias: session.alias,
    userId: String(session.user.id),
    'outlook-version': getOutlookClientType(),
    'addin-version': contextService.version ?? uuidv4(),
  };

  const customProperties = {
    ...baseCustomDimensions,
    ...currentInsights.customProperties,
  };

  globalStore.setInsightsConfiguration({
    ...state.insightsConfiguration,
    default: {
      ...currentInsights,
      eventLevel,
      customProperties,
    },
  });
}

function addCustomProperty(
  key: string,
  value: string | undefined,
  customProperties: Record<string, string> | undefined,
): Record<string, string> {
  customProperties ??= {};
  if (!value) return customProperties;
  return {
    ...customProperties,
    [key]: value,
  };
}

function initializeVerboseCustomProperties(
  globalStore: GlobalStore,
  outlookStore: OutlookStore,
): void {
  const state = getState(globalStore);
  const currentInsights = state.insightsConfiguration['default'];
  if (currentInsights?.eventLevel !== SeverityLevel.Verbose) {
    return;
  }

  const sync = outlookStore.syncConfiguration();
  const vertical = String(outlookStore.vertical());
  const language = outlookStore.language();
  let customProperties = currentInsights.customProperties;
  customProperties = addCustomProperty(
    'userSyncConfigurationId',
    sync?.id,
    customProperties,
  );
  customProperties = addCustomProperty('vertical', vertical, customProperties);
  customProperties = addCustomProperty('language', language, customProperties);

  globalStore.setInsightsConfiguration({
    ...state.insightsConfiguration,
    default: {
      ...currentInsights,
      customProperties,
    },
  });
}

// Eagerly injects OutlookService whose constructor reads `Office.context.mailbox`.
// Only safe in Office.js taskpane host; excluded from browser-only routes.
const officeMailboxInitializer = provideAppInitializer(async () => {
  const outlookService = inject(OutlookService);
  const outlookStore = inject(OutlookStore);

  const officeContext = getOfficeContextSafe();
  if (!officeContext?.mailbox) return;

  const isSharedMailbox = await outlookService.isSharedMailbox();
  outlookStore.setIsSharedMailbox(isSharedMailbox);
  outlookStore.setMailboxType(
    isSharedMailbox ? MailboxType.Shared : MailboxType.Personal,
  );

  const isSentFolder = await outlookService.isSentFolder();
  outlookStore.setIsSentFolder(isSentFolder);
});

// Browser-only `/sync-configuration` swaps this for a passive initializer that never refreshes tokens.
export const taskpaneAuthInitializer = provideAppInitializer(async () => {
  const verticalService = inject(VerticalService);
  const initializeDatabaseVertical =
    createDatabaseVerticalInitializer(verticalService);
  const initializationService = inject(InitializationService);

  const authService = inject(OutlookAuthService);
  const syncFacade = inject(SyncConfigurationFacade);
  const outlookStore = inject(OutlookStore);
  const globalStore = inject(GlobalStore);
  const oauthService = inject(OAuth2Service);
  const preferencesService = inject(PreferencesService);
  const abEntryService = inject(AbEntryService);
  const featureService = inject(FeatureService);
  const contextService = inject(ContextService);
  const insightsService = inject(InsightsService);

  await authService.initialize();

  const sessionInfo = globalStore.session();
  const isCloud = oauthService.getIsCloudFromStorage();

  outlookStore.setIsCloud(isCloud);
  if (!isCloud) {
    insightsService.disable();
  } else if (sessionInfo && contextService.isCloud) {
    insightsService.enable();
  }
  if (!sessionInfo) {
    initializationService.stopInitialization();
    return;
  }

  initializeMinimumLogLevel(globalStore, featureService, contextService);
  outlookStore.setSaveAppointmentShown(
    featureService.isFeatureOn(
      'integration-o365-save-appointment-shown',
      false,
    ),
  );

  await initializeDatabaseVertical();

  const syncConfig = await initializeSyncConfiguration(
    syncFacade,
    outlookStore,
    sessionInfo,
    isCloud,
  );

  initializeVerboseCustomProperties(globalStore, outlookStore);

  await initializeUserPreferences(
    preferencesService,
    abEntryService,
    outlookStore,
    globalStore,
    sessionInfo,
    isCloud,
    syncConfig,
  );

  initializationService.stopInitialization();
});

export const outlookCoreProvidersBase: Array<Provider | EnvironmentProviders> =
  [
    // Core Angular providers
    provideZoneChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideInsights(),
    provideKendo(),

    // State management
    provideStore(
      {},
      {
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      },
    ),
    provideEffects([]),
    ...(environment.production ? [] : [provideStoreDevtools()]),

    // Translation
    provideTranslateServiceForApp(),

    //OAuth
    provideOAuth2DataAccess({
      globalServicesKey: environment.oauth.globalServicesKey,
      globalServicesUrl: environment.oauth.globalServicesUrl,
      oauth2ClientId: environment.oauth.oauth2ClientId,
      oauth2ClientIdEmea: environment.oauth.oauth2ClientIdEmea,
    }),

    // Domain providers
    provideSharedDataAccess({
      debug: !environment.production,
      featureClientId: environment.launchDarklyClientId,
      smartFill: environment.smartFill,
      appVersion: ADDIN_VERSION,
      product: environment.product,
      oauth2: environment.oauth,
    }),
    provideAiDataAccess({
      environment: environment.environment,
      integrationWebAppUrl: environment.integrationApp.webAppUrl,
      integrationIqBoostUrl: environment.integrationApp.iqBoostApiUrl,
      integrationSubscriptionKey: environment.integrationApp.subscriptionKey,
    }),
    // The add-in refreshes tokens via OAuth2, not the core-web session endpoint.
    { provide: TokenService, useClass: OutlookAiTokenService },
    provideCoreGlobalSearchDataAccess(),
    provideCoreSettingsDataAccess(),
    provideOutlookCaseDataAccess(),
    provideOutlookInteractionLogDataAccess(),
    provideOutlookMainDataAccess(),
    provideOutlookNoteDataAccess(),
    provideOutlookOpportunityDataAccess(),
    provideOutlookSharedDataAccess({
      integrationWebhookUrl: environment.integration.webhookUrl,
      graphApiScopes: environment.graphApiConfig.scopes,
      graphApiUri: environment.graphApiConfig.uri,
      onPremClientId: environment.oauth.onPremClientId,
      httpTimeout: environment.httpConfiguration.timeout,
      httpRetryAmount: environment.httpConfiguration.retryAmount,
    }),
    provideOutlookSyncDataAccess({
      apiUrl: environment.integration.syncApi,
      subscriptionKey: environment.integration.subscriptionKey,
      appStoreOAuthUrl: environment.appStoreOAuthUrl,
      appStoreConfigurationUrl: environment.appStoreConfigurationUrl,
    }),
    provideOutlookTaskDataAccess(),
    provideOutlookTimelineDataAccess(),

    // Configuration tokens
    { provide: CDN_URL, useValue: environment.cdn },
    { provide: INSTRUMENTATION_KEY, useValue: environment.instrumentationKey },
    { provide: APP_STORE_OAUTH_URL, useValue: environment.appStoreOAuthUrl },
    {
      provide: APP_STORE_CONFIGURATION_URL,
      useValue: environment.appStoreConfigurationUrl,
    },
    {
      provide: INTEGRATION_WEBHOOK_URL,
      useValue: environment.integration.webhookUrl,
    },
    {
      provide: GRAPH_API_SCOPES,
      useValue: environment.graphApiConfig.scopes,
    },
    {
      provide: GRAPH_API_URI,
      useValue: environment.graphApiConfig.uri,
    },
    {
      provide: OPEN_AI,
      useValue: environment.openAi,
    },
    {
      provide: LOCALE_ID,
      useFactory: () => getOfficeLocale(),
    },
    // MSAL (Azure AD authentication)
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    MsalService,
    MsalBroadcastService,
    {
      provide: MessageService,
      useFactory: () => {
        // Messages service used to translate kendo component strings
        if (isOfficeFrench()) {
          return inject(FrenchMessageService);
        }
        return new MessageService(); // Default
      },
    },
    provideAppInitializer(() => {
      const initializationService = inject(InitializationService);
      const globalStore = inject(GlobalStore);
      const insightsService = inject(InsightsService);
      const oauthService = inject(OAuth2Service);

      initializationService.startInitialization();

      if (oauthService.getIsCloudFromStorageWithFallback() !== true) {
        insightsService.disable();
      }

      globalStore.setInsightsConfiguration({
        default: {
          applicationInsights: {
            instrumentationKey: environment.instrumentationKey,
            disableFlushOnBeforeUnload: true,
            disableFlushOnUnload: true,
          },
          eventLevel: SeverityLevel.Information,
          trackPageView: true,
          customProperties: {
            'addin-version': ADDIN_VERSION,
          },
        },
      });
    }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const outlookStore = inject(OutlookStore);

      const detectedLocale = getOfficeLocale();
      outlookStore.setLanguage(detectedLocale);

      return new Promise<void>((resolve) => {
        translate.use(detectedLocale).subscribe(() => {
          resolve();
        });
      });
    }),
  ];

export const appConfig: ApplicationConfig = {
  providers: [
    ...outlookCoreProvidersBase,
    taskpaneAuthInitializer,
    officeMailboxInitializer,
  ],
};
